// Created: shared utility for validating and converting image uploads to WebP
// Supports .webp/.png/.jpeg up to 20MB, converts non-webp images to WebP at 80% quality
export const MAX_IMAGE_SIZE_BYTES = 20 * 1024 * 1024;
export const WEBP_QUALITY = 0.8;

const ACCEPTED_TYPES = ['image/webp', 'image/png', 'image/jpeg', 'image/jpg'];

export type ValidationResult =
  | { ok: true }
  | { ok: false; message: string };

export const validateImageFile = (file: File): ValidationResult => {
  const type = file.type.toLowerCase();
  const isAccepted = ACCEPTED_TYPES.some((t) => type === t || type.includes(t.split('/')[1]));
  if (!isAccepted) {
    return { ok: false, message: 'Alleen .webp, .png en .jpg bestanden zijn toegestaan' };
  }
  if (file.size > MAX_IMAGE_SIZE_BYTES) {
    return { ok: false, message: 'Bestand mag maximaal 20MB zijn' };
  }
  return { ok: true };
};

export const convertToWebp = async (file: File): Promise<File> => {
  if (file.type.toLowerCase() === 'image/webp') {
    return file;
  }

  const dataUrl: string = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Kan bestand niet lezen'));
    reader.readAsDataURL(file);
  });

  const img: HTMLImageElement = await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Kan afbeelding niet laden'));
    image.src = dataUrl;
  });

  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas context niet beschikbaar');
  }
  ctx.drawImage(img, 0, 0);

  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) resolve(b);
        else reject(new Error('Conversie naar WebP mislukt'));
      },
      'image/webp',
      WEBP_QUALITY
    );
  });

  const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
  return new File([blob], `${baseName}.webp`, { type: 'image/webp' });
};
